import React, { useEffect, useMemo, useRef, useState } from "react";
import "./ConfigureGameSettings.css";
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from "react-router-dom";
import { updateGameSettings } from "../../functions/updateGameSettings";
import { selectAdminToken } from "../../admin/sessionSlice";
import { setBackButtonUrl } from '../uiSlice';
import Swal from "sweetalert2";
import logoImg from "../images/desktop/logo.png";
import { validateLobbyPin } from "../../functions/validateLobbyPin";

const PresenterIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

const PersonalIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="8" r="4" />
    <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" />
  </svg>
);

const AnchorIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="3" width="20" height="14" rx="2" />
    <path d="M8 21h8M12 17v4" />
  </svg>
);

const MonitorWifiIcon = () => (
  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="7" width="20" height="13" rx="2" />
    <path d="M8 21h8M12 17v4" />
    <path d="M5 3.5C7.5 2 9.5 1.5 12 1.5s4.5.5 7 2" />
    <path d="M7.5 5.5C9 4.5 10.5 4 12 4s3 .5 4.5 1.5" />
  </svg>
);

const ClockIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <path d="M12 6v6l4 2" />
  </svg>
);

const InfoIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <path d="M12 16v-4M12 8h.01" />
  </svg>
);

const ArrowIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 12h14M12 5l7 7-7 7" />
  </svg>
);

// ── Mode descriptions ────────────────────────────────────────────────────────
const MODE_DESCRIPTIONS = {
  Manager: "Manager mode is ideal for hosting and engaging with your audience in real time.",
  Personal: "Personal mode lets each participant play at their own pace independently.",
  Anchor: "Anchor mode gives you full control — share your screen with participants on Zoom or Teams while they interact in real-time.",
};

const MIN_POINTS = 1;
const MAX_POINTS = 100;

// ── Component ────────────────────────────────────────────────────────────────
const ConfigureGameSettings = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const user = useSelector((s) => s.auth?.user);
  const adminToken = useSelector(selectAdminToken);

  // These APIs require `Authorization: Bearer ${sessionId}&${organizationId}`.
  const tokenForApi = useMemo(() => {
    const t = adminToken != null ? String(adminToken).trim() : "";
    if (t.includes("&")) return t;
    const sid = user?.sessionId ?? user?.sessionid ?? "";
    const oid = user?.organizationId ?? user?.orgId ?? user?.organizationid ?? "";
    return sid && oid ? `${sid}&${oid}` : "";
  }, [adminToken, user]);

  const [mode, setMode] = useState("Manager");
  const [questions, setQuestions] = useState(10);
  const [time, setTime] = useState(1);
  const [anchorOn, setAnchorOn] = useState(false);
  const [points, setPoints] = useState(10);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const reportIdRef = useRef(null);
  const stageIdRef = useRef(null);
  const maxQuestionsWarnedRef = useRef(false);
  const [joinPin, setJoinPin] = useState("");
  const [joinLoading, setJoinLoading] = useState(false);
  const MAX_QUESTIONS = 20;

  useEffect(() => {
    dispatch(setBackButtonUrl("/login?&save=true"));
  }, [user]);

  const decreaseQuestions = () => {
    setQuestions((q) => Math.max(1, (Number(q) || 1) - 1));
  };
  const increaseQuestions = async () => {
    let hitMax = false;
    setQuestions((q) => {
      const next = Math.min(MAX_QUESTIONS, (Number(q) || 0) + 1);
      hitMax = next >= MAX_QUESTIONS;
      return next;
    });
    if (hitMax && !maxQuestionsWarnedRef.current) {
      maxQuestionsWarnedRef.current = true;
      await Swal.fire({
        icon: "warning",
        title: "Max Limit Reached",
        text: `Maximum ${MAX_QUESTIONS} questions allowed.`,
        confirmButtonColor: "#2563eb",
      });
      // allow warning again only after user goes below max
    }
  };

  // Track slider progress for custom fill
  const sliderStyle = {
    "--progress": `${((time - 1) / 9) * 100}%`,
  };

  const settings = useMemo(() => {
    const q = Math.max(1, Math.min(MAX_QUESTIONS, Number(questions) || MAX_QUESTIONS));
    const t = Number(time) || 5;
    const p = Math.max(MIN_POINTS, Math.min(MAX_POINTS, Number(points) || MIN_POINTS));
    return { mode, questions: q, time: t, points: p, isAnchorMode: mode === "Anchor" && anchorOn };
  }, [mode, questions, time, points, anchorOn]);

  useEffect(() => {
    if (Number(questions) < MAX_QUESTIONS) maxQuestionsWarnedRef.current = false;
  }, [questions]);

  // Hydrate from local cache
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem("gameSettings");
      if (!raw) return;
      const cached = JSON.parse(raw);
      if (cached?.mode) setMode(String(cached.mode));
      if (cached?.questions != null && Number.isFinite(Number(cached.questions)))
        setQuestions(Math.max(1, Math.min(MAX_QUESTIONS, Number(cached.questions))));
      if (cached?.time != null && Number.isFinite(Number(cached.time)))
        setTime(Number(cached.time));
      if (cached?.points != null && Number.isFinite(Number(cached.points)))
        setPoints(Math.max(MIN_POINTS, Math.min(MAX_POINTS, Number(cached.points))));
      if (cached?.reportId) reportIdRef.current = String(cached.reportId);
      if (cached?.stageId) stageIdRef.current = String(cached.stageId);
    } catch {
      // ignore
    }
  }, []);

  // Anchor mode: force toggle ON when mode is selected
  useEffect(() => {
    if (mode === "Anchor") {
      setAnchorOn(true);
    }
  }, [mode]);

  const wrongLobbyPinPopup = async () => {
    await Swal.fire({
      icon: "error",
      title: "Wrong Lobby PIN",
      text: "Please check the PIN and try again.",
      confirmButtonText: "OK",
      background: "linear-gradient(135deg,#dbeafe,#bfdbfe)",
      color: "#1e3a8a",
      confirmButtonColor: "#2563eb",
    });
  };

  const handleJoinLobby = async () => {
    const pin = String(joinPin || "").trim();
    if (!pin || !/^\d{5}$/.test(pin)) {
      await Swal.fire({
        icon: "warning",
        title: "Enter Lobby PIN",
        text: "PIN must be 5 digits.",
        confirmButtonColor: "#2563eb",
      });
      return;
    }
    if (!tokenForApi) return;
    setJoinLoading(true);
    try {
      const resp = await validateLobbyPin({ token: tokenForApi, lobby_pin: pin });
      if (!resp?.match) {
        await wrongLobbyPinPopup();
        return;
      }
      const uid = user?.userId ?? user?.userid ?? user?.id;
      if (!uid) throw new Error("Missing userId");

      // resp.stageId is the host stage row; do NOT use it for the joining user.
      // Attach lobby_name/pin to the current user's existing stage row (so we update, not insert).
      let existingStageId = stageIdRef.current;
      if (!existingStageId) {
        try {
          const cached = JSON.parse(sessionStorage.getItem("gameSettings") || "{}");
          existingStageId = cached?.stageId ?? null;
        } catch {
          existingStageId = null;
        }
      }
      const joinTheme = resp.themeName ?? "guess_me";
      const upd = await updateGameSettings({
        token: tokenForApi,
        userId: uid,
        ...(existingStageId ? { stageId: existingStageId } : {}),
        themeName: joinTheme,
        lobby_name: resp.lobby_name ?? null,
        lobby_pin: resp.lobby_pin ?? pin,
        show_question: true,
        ...(resp.lifelines_enabled != null ? { lifelines_enabled: resp.lifelines_enabled } : {}),
        ...(resp.lifeline_fifty_fifty != null ? { lifeline_fifty_fifty: resp.lifeline_fifty_fifty } : {}),
        ...(resp.lifeline_double_dip != null ? { lifeline_double_dip: resp.lifeline_double_dip } : {}),
        ...(resp.lifeline_flip != null ? { lifeline_flip: resp.lifeline_flip } : {}),
        ...(resp.shuffle_questions != null ? { shuffle_questions: resp.shuffle_questions } : {}),
        ...(resp.time_duration != null ? { time_duration: resp.time_duration } : {}),
        ...(resp.points != null ? { points: resp.points } : {}),
      });
      if (upd?.stageId) stageIdRef.current = String(upd.stageId);

      // Save all lobby info locally for QuizUi
      const lobbyData = {
        ...settings,
        reportId: reportIdRef.current,
        lobby_name: resp.lobby_name ?? null,
        lobby_pin: resp.lobby_pin ?? pin,
        stageId: stageIdRef.current ?? null,
        themeName: joinTheme,
        hostUserId: resp.hostUserId ?? null,
        lobby_link: resp.lobby_link ?? null,
        lifelines_enabled: resp.lifelines_enabled ?? null,
        lifeline_fifty_fifty: resp.lifeline_fifty_fifty ?? null,
        lifeline_double_dip: resp.lifeline_double_dip ?? null,
        lifeline_flip: resp.lifeline_flip ?? null,
      };
      sessionStorage.setItem("gameSettings", JSON.stringify(lobbyData));
      // Navigate directly to quiz with all necessary data in state
      navigate("/quiz-ui", {
        replace: true,
        state: {
          lobby_name: resp.lobby_name,
          stageId: stageIdRef.current,
          themeName: joinTheme,
          lobby_pin: resp.lobby_pin ?? pin,
          backTo: "/configure-game-settings",
        },
      });
    } catch (e) {
      await Swal.fire("Error", e?.message || "Failed to join lobby", "error");
    } finally {
      setJoinLoading(false);
    }
  };

  // Create initial DB entry once (stages row), then update it with settings.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      try {
        setSaveError("");
        setSaving(true);

        if (cancelled) return;

        // Insert (if needed) + update settings.
        const uid = user?.userId ?? user?.userid ?? user?.id;
        if (tokenForApi && uid) {
          const upd = await updateGameSettings({
            token: tokenForApi,
            userId: uid,
            stageId: stageIdRef.current,
            themeName: "guess_me",
            user_type: user?.user_type ?? user?.role ?? null,
            current_mode: mode,
            questions_no: settings.questions,
            time_duration: time,
            points: settings.points,
            show_question: true,
            email: user?.email ?? null,
            fullName: user?.fullName ?? user?.name ?? user?.username ?? null,
            employeeId: user?.employeeId ?? user?.employeeid ?? null,
            reportid: user?.reportId ?? user?.reportid ?? null,
          });
          if (upd?.stageId != null) stageIdRef.current = String(upd.stageId);
          sessionStorage.setItem(
            "gameSettings",
            JSON.stringify({
              ...settings,
              reportId: reportIdRef.current,
              lobby_name: upd?.lobby_name ?? null,
              lobby_pin: upd?.lobby_pin ?? null,
              stageId: stageIdRef.current,
              isAnchorMode: mode === "Anchor" && anchorOn,
            })
          );
          return;
        }
        sessionStorage.setItem(
          "gameSettings",
          JSON.stringify({ ...settings, reportId: reportIdRef.current, isAnchorMode: mode === "Anchor" && anchorOn })
        );
      } catch (e) {
        if (!cancelled) setSaveError(e?.message || "Failed to save settings");
      } finally {
        if (!cancelled) setSaving(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, adminToken]);

  const handleNext = async () => {
    if (!user) {
      navigate("/login", { replace: true });
      return;
    }
    try {
      setSaveError("");
      setSaving(true);
      // Only update existing row; do not insert again here.
      const uid = user?.userId ?? user?.userid ?? user?.id;

      // For Anchor mode, force anchorOn to true
      const finalAnchorMode = mode === "Anchor" ? true : anchorOn;

      const upd = await updateGameSettings({
        token: tokenForApi,
        userId: uid,
        stageId: stageIdRef.current,
        themeName: "guess_me",
        user_type: user?.user_type ?? user?.role ?? null,
        current_mode: mode,
        questions_no: settings.questions,
        time_duration: time,
        points: settings.points,
        show_question: true,
        email: user?.email ?? null,
        fullName: user?.fullName ?? user?.name ?? user?.username ?? null,
        employeeId: user?.employeeId ?? user?.employeeid ?? null,
        reportid: user?.reportId ?? user?.reportid ?? null,
      });
      if (upd?.stageId != null) stageIdRef.current = String(upd.stageId);

      const settingsToStore = {
        ...settings,
        reportId: reportIdRef.current,
        lobby_name: upd?.lobby_name ?? null,
        lobby_pin: upd?.lobby_pin ?? null,
        stageId: stageIdRef.current,
        isAnchorMode: mode === "Anchor" && finalAnchorMode,
      };

      sessionStorage.setItem("gameSettings", JSON.stringify(settingsToStore));

      const nextPath = String(mode || "").toLowerCase() === "manager" ? "/manager-dashboard" : "/manager-dashboard";

      navigate(nextPath, {
        replace: true,
        state: { reportId: reportIdRef.current, settings },
      });
    } catch (e) {
      setSaveError(e?.message || "Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="config-container">
      <h1 className="title">Configure Game Settings</h1>
      <p className="subtitle">Customize your game experience before you start.</p>

      <div className="cards-wrapper">
        {/* ── Left Card ── */}
        <div className="glass-card">
          <h2>Game Mode</h2>

          {/* Mode toggle */}
          <div className="mode-toggle">
            <button
              className={mode === "Manager" ? "active" : ""}
              onClick={() => setMode("Manager")}
            >
              <PresenterIcon />
              <span className="btn-label">Manager</span>
            </button>
            <button
              className={mode === "Personal" ? "active" : ""}
              onClick={() => setMode("Personal")}
            >
              <PersonalIcon />
              <span className="btn-label">Personal</span>
            </button>
            <button
              className={mode === "Anchor" ? "active" : ""}
              onClick={() => setMode("Anchor")}
            >
              <AnchorIcon />
              <span className="btn-label">Anchor</span>
            </button>
          </div>

          <p className="mode-description">{MODE_DESCRIPTIONS[mode]}</p>

          {/* Anchor Mode feature card */}
          {mode === "Anchor" && (
            <div className="anchor-feature-card">
              <div className="anchor-icon-box">
                <MonitorWifiIcon />
              </div>
              <div className="anchor-feature-body">
                <div className="anchor-feature-header">
                  <span className="anchor-feature-title">
                    Anchor Mode
                    <span className="live-badge">● LIVE</span>
                  </span>
                </div>
                <p className="anchor-feature-desc">
                  Take control of the session. Share your screen with participants
                  on Zoom or Teams while they interact in real-time.
                </p>
              </div>
            </div>
          )}

          {mode === "Personal" && (
            <div className="question-toggle-row" style={{ marginTop: 8 }}>
              <div style={{ flex: 1 }}>
                <h3>Join a Lobby</h3>
                <p>Enter the 5-digit Lobby PIN to join an existing lobby.</p>
                <input
                  value={joinPin}
                  onChange={(e) => setJoinPin(e.target.value.replace(/[^\d]/g, "").slice(0, 5))}
                  placeholder="Lobby PIN (5 digits)"
                  style={{
                    width: "100%",
                    marginTop: 10,
                    padding: "12px 14px",
                    borderRadius: 12,
                    border: "1.5px solid #d4dcf8",
                    background: "#f8f9fe",
                    fontWeight: 800,
                    outline: "none",
                    fontSize: 16,
                  }}
                />
              </div>
              <button
                type="button"
                onClick={() => void handleJoinLobby()}
                disabled={joinLoading}
                style={{
                  height: 48,
                  alignSelf: "flex-end",
                  padding: "0 18px",
                  borderRadius: 14,
                  border: "none",
                  background: "linear-gradient(135deg,#4f9ef8,#2563eb)",
                  color: "white",
                  fontWeight: 900,
                  cursor: "pointer",
                  opacity: joinLoading ? 0.7 : 1,
                  fontSize: 15,
                  fontFamily: "inherit",
                }}
              >
                {joinLoading ? "JOINING..." : "JOIN"}
              </button>
            </div>
          )}
        </div>

        {/* ── Right Card ── */}
        <div className="glass-card">
          <h2>Game Settings</h2>

          <div className="rounds-section">
            <label>Questions</label>
            <div className="round-box">
              <button onClick={decreaseQuestions}>−</button>
              <input
                type="number"
                min="1"
                max={MAX_QUESTIONS}
                value={questions}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === "") {
                    setQuestions("");
                    return;
                  }
                  const numValue = Number(value);
                  if (numValue > MAX_QUESTIONS) {
                    Swal.fire({
                      icon: "warning",
                      title: "Max Limit Reached",
                      text: `Maximum ${MAX_QUESTIONS} questions allowed.`,
                      confirmButtonColor: "#2563eb",
                    });
                    setQuestions(MAX_QUESTIONS);
                    return;
                  }
                  if (numValue < 1) {
                    setQuestions(1);
                    return;
                  }
                  setQuestions(numValue);
                }}
              />
              {/* <button onClick={increaseQuestions}>+</button> */}
              <button 
  onClick={increaseQuestions}
  disabled={questions >= MAX_QUESTIONS}
>
  +
</button>
            </div>
          </div>

          {mode !== "Anchor" && (
            <>
              <div className="rounds-section">
                <label>Points</label>
                <div className="round-box">
                  <button type="button" onClick={() => setPoints((p) => Math.max(MIN_POINTS, Math.min(MAX_POINTS, Number(p || MIN_POINTS) - 1)))}>−</button>
                  <input
                    type="number"
                    min={MIN_POINTS}
                    max={MAX_POINTS}
                    value={points}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === "") return setPoints("");
                      const n = Number(value);
                      if (!Number.isFinite(n)) return;
                      setPoints(Math.max(MIN_POINTS, Math.min(MAX_POINTS, n)));
                    }}
                    onBlur={() => {
                      setPoints((p) => {
                        const n = Number(p);
                        if (!Number.isFinite(n) || p === "") return MIN_POINTS;
                        return Math.max(MIN_POINTS, Math.min(MAX_POINTS, n));
                      });
                    }}
                  />
                  <button type="button" onClick={() => setPoints((p) => Math.min(MAX_POINTS, Number(p || MIN_POINTS) + 1))}>+</button>
                </div>
              </div>

              <div className="duration-section">
                <label>
                  Set duration ( per question ) <ClockIcon />
                </label>
                <input
                  type="range"
                  min="1"
                  max="10"
                  value={time}
                  style={sliderStyle}
                  onChange={(e) => setTime(Number(e.target.value))}
                />
                <div className="duration-display">
                  <ClockIcon /> {time} mins
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {saveError && <div className="error-msg">{saveError}</div>}

      <button className="next-btn" onClick={handleNext} disabled={saving}>
        {saving ? "SAVING..." : "NEXT"} {!saving && <ArrowIcon />}
      </button>
    </div>
  );
};

export default ConfigureGameSettings;
